import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Dialog } from "@actinc/dls";
import { Drag as DragIcon } from "@actinc/dls/icons";
import { Box, DialogTitle, Paper } from "@mui/material";

const DRAG_HANDLE_CLASS = "DraggableDialog-dragHandle";

const clamp = (value, min, max) =>
  min > max ? value : Math.max(min, Math.min(max, value));

// Keeps the dialog's edges within the current viewport. `edgeOffsets` are
// captured relative to the untransformed position at drag start, so the
// viewport size is read fresh here rather than frozen at that point.
const clampToViewport = (nextX, nextY, edgeOffsets) => {
  if (!edgeOffsets) return { x: nextX, y: nextY };

  return {
    x: clamp(nextX, -edgeOffsets.left, window.innerWidth - edgeOffsets.right),
    y: clamp(nextY, -edgeOffsets.top, window.innerHeight - edgeOffsets.bottom),
  };
};

const DraggablePaper = forwardRef((props, ref) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragStateRef = useRef(null);
  const paperRef = useRef(null);

  const setRefs = useCallback(
    (node) => {
      paperRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const handlePointerDown = useCallback(
    (event) => {
      const handle = event.target.closest?.(`.${DRAG_HANDLE_CLASS}`);
      if (!handle) return;
      if (event.target.closest?.("button, a, input, textarea, select")) return;

      const dialogRect = paperRef.current?.getBoundingClientRect();
      dragStateRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        originX: position.x,
        originY: position.y,
        edgeOffsets: dialogRect
          ? {
              left: dialogRect.left - position.x,
              right: dialogRect.right - position.x,
              top: dialogRect.top - position.y,
              bottom: dialogRect.bottom - position.y,
            }
          : null,
      };
      event.preventDefault();
    },
    [position.x, position.y],
  );

  useEffect(() => {
    const handleMove = (event) => {
      if (!dragStateRef.current) return;
      const { startX, startY, originX, originY, edgeOffsets } =
        dragStateRef.current;
      const nextX = originX + event.clientX - startX;
      const nextY = originY + event.clientY - startY;

      setPosition(clampToViewport(nextX, nextY, edgeOffsets));
    };
    const handleUp = () => {
      dragStateRef.current = null;
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, []);

  const { sx: paperSx, ...rest } = props;

  return (
    <Paper
      {...rest}
      ref={setRefs}
      onPointerDown={handlePointerDown}
      sx={{
        ...(paperSx || {}),
        transform: `translate(${position.x}px, ${position.y}px)`,
      }}
    />
  );
});
DraggablePaper.displayName = "DraggablePaper";

const DraggableDialogTitle = ({ children, id, sx, ...rest }) => (
  <DialogTitle
    id={id}
    className={DRAG_HANDLE_CLASS}
    sx={{
      cursor: "move",
      userSelect: "none",
      display: "flex",
      alignItems: "center",
      gap: 1,
      py: 1,
      px: 2,
      fontSize: "1rem",
      bgcolor: "primary.main",
      color: "primary.contrastText",
      ...sx,
    }}
    {...rest}
  >
    <Box aria-hidden="true" sx={{ display: "flex", alignItems: "center" }}>
      <DragIcon fontSize="small" />
    </Box>
    {children}
  </DialogTitle>
);

const DraggableDialog = ({
  children,
  modeless = true,
  onClose,
  title,
  titleId,
  ...dialogProps
}) => {
  const generatedTitleId = useId();
  const resolvedTitleId = titleId ?? generatedTitleId;

  const handleDialogClose = useCallback(
    (event, reason) => {
      if (modeless && reason === "backdropClick") return;
      onClose?.(event, reason);
    },
    [modeless, onClose],
  );

  const modelessProps = modeless
    ? {
        hideBackdrop: true,
        disableEnforceFocus: true,
        disableScrollLock: true,
        sx: {
          ...(dialogProps.sx || {}),
          pointerEvents: "none",
          "& .MuiDialog-container": { pointerEvents: "none" },
          "& .MuiPaper-root": { pointerEvents: "auto" },
        },
      }
    : {};

  return (
    <Dialog
      onClose={handleDialogClose}
      PaperComponent={DraggablePaper}
      aria-labelledby={resolvedTitleId}
      {...dialogProps}
      {...modelessProps}
    >
      {title !== undefined && (
        <DraggableDialogTitle id={resolvedTitleId}>
          {title}
        </DraggableDialogTitle>
      )}
      {children}
    </Dialog>
  );
};

export { DraggableDialog, DraggableDialogTitle, DRAG_HANDLE_CLASS };
